
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import HeaderTitle from './HeaderTitle.vue';
import ActionButton from './ActionButton.vue';
import StoresSwitcher from './StoresSwitcher.vue';
import UpgradeModal from './UpgradeModal.vue';
import PinButton from './PinButton.vue';
import PinnedSection from './PinnedSection.vue';
import { StoreModel } from '@/models/store';
import { EventBus } from '@/main';
import ForecastSetting from '@/models/forecast_setting';
import { FEATURES } from '@/config/featureConfig';
import { replenishmentV2 } from '../config/menu/replenishment-v2';
import { freeV2 } from '../config/menu/free-v2';
import { analyticsV1 } from '../config/menu/analytics-v1';
import { replenishmentV1 } from '../config/menu/replenishment-v1';
import { freeV1 } from '../config/menu/free-v1';
import { getModule } from 'vuex-module-decorators';
import MenuVersionModule from '@/store/modules/menu-version';
import PinnedItemsModule from '@/store/modules/pinned-items';
import store from '@/store';
import { EventHandler } from '@/modules/events';

@Component({
components: {
  HeaderTitle,
  ActionButton,
  StoresSwitcher,
  UpgradeModal,
  PinButton,
  PinnedSection,
},
})
export default class VerticalMenuBar extends Vue {

// Lazy initialization of EventHandler
private get eventHandler(): EventHandler {
  if (!this._eventHandler) {
    this._eventHandler = new EventHandler({ store: this.store });
  }
  return this._eventHandler;
}

public get getMenuItems() {
  const menuVersion = this.menuVersionStore.menuVersion;
  const packageName = this.store.currentPackage.name;

  if (menuVersion === 1) {
    if (this.isFreeUser()) {
      return this.addVersionSwitcher(freeV1.data);
    } else if (packageName && packageName.toLowerCase().includes('replenishment')) {
      return this.addVersionSwitcher(replenishmentV1.data);
    } else {
      return this.addVersionSwitcher(analyticsV1.data);
    }
  } else {
    if (this.isFreeUser()) {
      return this.addVersionSwitcher(freeV2.data);
    } else {
      return this.addVersionSwitcher(replenishmentV2.data);
    }
  }
}

get canAccessOperationalMode(): boolean {
  const validateValues = [
    {
      code: 'operational_mode',
      value: 'unlocked',
    },
  ];
  const validateResult = this.store.validateRule(validateValues);
  return validateResult.isValidate;
}

public get isTestingStore() {
  const testingStores = [
    'dariustest2.myshopify.com',
    'the-best-mega-store.myshopify.com',
    '2dba1f-4.myshopify.com',
  ];
  return testingStores.includes(this.store.shopName);
}

public get isAssistyAdmin() {
  if (localStorage.getItem('loggedInFromHades') !== 'true') {
    return false;
  }
  return true;
}
@Prop({ default: () => new StoreModel() })
public store!: StoreModel;
public mini: boolean = true;
@Prop({default: null})
public app!: any;
public search: string = '';
public menuPurpose: string = '';
public canExpandOnHover: boolean = true;
private forecastSetting = new ForecastSetting();
private currentMethodology: string = '';
private menuVersionStore = getModule(MenuVersionModule, store);
private pinnedItemsStore = PinnedItemsModule;
private _eventHandler: EventHandler | null = null;
private pageLoadTime: number = Date.now();
private upgradeModalShownTime: number = 0;

// Enhanced state management for menu expansion
private expandedState = {
  sections: {} as { [key: string]: boolean },
  groups: {} as { [key: string]: boolean },
};

// Track current menu location (using titles only)
private currentMenuPath: {
  section?: string;
  group?: string;
  item?: string;
} = {};

// Track manually collapsed items to respect user preference
private manuallyCollapsed = new Set < string > ();

// Keep for backward compatibility temporarily
private expandedSections: { [key: string]: boolean } = {};

// Add data property
private showUpgradeModal: boolean = false;
private selectedLockedFeature: any = null;

public isFreeUser(): boolean {
  return this.store.currentPackage.name === 'Free';
}

public pinMenu() {
  this.canExpandOnHover = !this.canExpandOnHover;
  const isPinned = !this.canExpandOnHover;
  EventBus.$emit('vertical-menu-pinned', isPinned); // Emit this first
  if (isPinned) { // Menu is being pinned (mini = false, canExpandOnHover = false)
    this.mini = false;
    EventBus.$emit('vertical-menu-expanded');
  } else { // Menu is being unpinned (mini = true, canExpandOnHover = true)
    this.mini = true;
  }
  // EventBus.$emit('pin-menu', this.canExpandOnHover); // Old event can likely be removed if nothing else uses it
}

public searchReport() {
  this.$router.push('/report-library?search=' + this.search);
  this.search = '';
}

public doLogout() {
  this.$emit('logout');
}

public handleMenuClick(link: any, section?: string, group?: string, item?: string) {
  console.log('handleMenuClick called:', { link, section, group, item });

  // Track the menu navigation
  if (section) {
    this.trackMenuNavigation(section, group, item, link);
  }

  // Existing logout logic
  if (link === '/logout') {
    this.doLogout();
  }
}
public isPaidUser() {
  const isPaid: boolean = this.store.currentPackage.name !== 'Free';
  return isPaid;
}

public isCustomIcon(icon: string) {
  return icon && !icon.includes('mdi-');
}

public async created() {
  EventBus.$on('report-setup-open', async (isOpen: any) => {

  });

  await this.loadForecastMethodology();

  // Load pinned items for current store
  if (this.store && this.store.id) {
    await this.pinnedItemsStore.loadPinnedItems(this.store.id);
  }

  // Load saved state from localStorage
  this.loadExpandedState();

  // If no saved state exists, expand the first section as default
  if (Object.keys(this.expandedState.sections).length === 0) {
    const firstSection = this.getMenuItems[0];
    if (firstSection) {
      this.$set(this.expandedState.sections, firstSection.section || 'replenishment', true);
      this.$set(this.expandedSections, firstSection.section || 'replenishment', true);
    }
  }

  // Restore expanded groups from localStorage
  Object.keys(this.expandedState.groups).forEach((groupKey) => {
    if (this.expandedState.groups[groupKey]) {
      // Force Vue to recognize the change
      this.$set(this.expandedState.groups, groupKey, true);
    }
  });

  // Also restore the old expandedSections for compatibility
  Object.keys(this.expandedState.sections).forEach((sectionKey) => {
    if (this.expandedState.sections[sectionKey]) {
      this.$set(this.expandedSections, sectionKey, true);
    }
  });

  EventBus.$on('report-drawer-opened', this.handleReportDrawerOpen);
  EventBus.$on('report-drawer-closed', this.handleReportDrawerClosed);
}

public loadMenuStage() {
    // Assuming 'isPinMenu' is the intended key. If 'appMenuPinned' is correct from App.vue, this should be updated.
    const isInitiallyPinned = localStorage.getItem('isPinMenu') === 'true';

    if (isInitiallyPinned) {
      this.mini = false;
      this.canExpandOnHover = false; // Ensure this is correctly set for the pinned state
      EventBus.$emit('vertical-menu-pinned', true);
      EventBus.$emit('vertical-menu-expanded'); // Emit new event
    } else {
      this.mini = true;
      this.canExpandOnHover = true; // Ensure this is correctly set for the unpinned state
      EventBus.$emit('vertical-menu-pinned', false);
    }
  }

@Watch('mini', { immediate: true, deep: true })
public onExpandMenu(newVal: any) {
  if (!this.mini) {
    // When menu expands from mini mode, restore the saved expanded state
    this.loadExpandedState();

    const reportPageRouteNames: any = ['scheduleEditReport', 'templateReport', 'templateReportPage', 'customReport', 'detailReportPage', 'previewScheduledReport', 'viewCardReport', 'templateReportByType'];
    const drawerOpen = localStorage.getItem('drawerOpen') === 'true';
    // check if this is the TemplateReportPage component
    if ((!reportPageRouteNames.includes(this.$route.name)) || (reportPageRouteNames.includes(this.$route.name) && !drawerOpen)) {
      this.pinMenu();
    }
  }
}

// Method to check permission using the service
public hasPermission(featureCode: string): boolean {
  // Ensure $permissionService exists and is initialized
  if (Vue.prototype.$permissionsInitializing === true) {
    console.log('Permissions initializing, denying access temporarily for', featureCode);
    return true; // Deny access while initializing to avoid flickering
  }
  if (this.$permissionService) {
    return this.$permissionService.hasPermission(featureCode);
  }
  console.warn('Permission service not available in VerticalMenuBar for check:', featureCode);
  return false; // Default to no permission if service is missing
}

// Simple lock check - reuse existing hasPermission method
public isItemLocked(item: any): boolean {
  return item.permissionRequired && !this.hasPermission(item.permissionCode);
}

// Get required package for display
public getRequiredPackage(item: any): string {
  // First check if item has lockInfo with requiredPackage
  if (item.lockInfo?.requiredPackage) {
    return item.lockInfo.requiredPackage;
  }

  // Fallback logic based on permission code
  if (item.permissionCode?.includes('replenishment')) {
    return 'Replenishment';
  }
  if (item.permissionCode?.includes('analytics')) {
    return 'Analytics';
  }
  return 'Professional';
}

// Helper method to check if an item is pinned
public isPinned(section: string, itemTitle: string, groupTitle?: string): boolean {
  const itemId = this.generateItemId(section, itemTitle, groupTitle);
  return this.pinnedItemsStore.isPinned(itemId);
}

// Handle pin/unpin toggle
public async handlePinToggle(itemData: any): Promise<void> {
  const isPinned = await this.pinnedItemsStore.togglePinItem({
    item: itemData,
    storeId: this.store.id,
  });

  // If item was pinned, expand the Pinned section
  if (isPinned) {
    // Wait for the DOM to update after the item is added
    await this.$nextTick();

    // Now set the expanded state
    const pinnedSection = this.$refs.pinnedSection as any;
    if (pinnedSection) {
      pinnedSection.expanded = true;
    }
  }

  // Optional: Show feedback (could add a snackbar/toast here)
  console.log(`Item ${isPinned ? 'pinned' : 'unpinned'}:`, itemData.title);
}

// Handle click on pinned item
public handlePinnedItemClick(item: any): void {
  // Track the click if needed
  console.log('Pinned item clicked:', item.title);
  // Navigation is handled by the router-link in PinnedSection component
}

// Handle item unpinned from pinned section
public handleItemUnpinned(item: any): void {
  console.log('Item unpinned from pinned section:', item.title);
  // Could show a toast/snackbar here
}

// Simple click handler for locked items
public handleItemClick(item: any): void {
  const timeSinceLoad = Date.now() - this.pageLoadTime;

  if (this.isItemLocked(item)) {
    // Track locked feature click
    const requiredPackage = item.lockInfo?.requiredPackage || this.getRequiredPackage(item);
    this.eventHandler.trackLockedFeatureClicked(item.title, requiredPackage);

    // Show upgrade modal
    this.selectedLockedFeature = {
      title: item.title,
      requiredPackage,
      description: item.lockInfo?.tooltipText || '',
      benefits: item.lockInfo?.benefits || [],
    };
    this.upgradeModalShownTime = Date.now();
    this.eventHandler.trackUpgradeModalShown(item.title, 'locked_feature');
    this.showUpgradeModal = true;
  } else {
    // Track menu item click
    this.eventHandler.trackMenuItemClicked(this.menuVersionStore.menuVersion, item.title, timeSinceLoad);

    // Normal navigation
    if (item.link) {
      this.$router.push(item.link);
    }
  }
}

public mounted() {
  this.initializeMenuVersion();
  // Track menu version loaded
  this.eventHandler.trackMenuVersionLoaded(this.menuVersionStore.menuVersion);

  // Cleanup old route mappings periodically
  this.cleanupOldRouteMappings();
}

public beforeDestroy() {
  EventBus.$off('report-drawer-opened', this.handleReportDrawerOpen);
  EventBus.$off('report-drawer-closed', this.handleReportDrawerClosed);
  // No need to EventBus.$off('vertical-menu-pinned') here as this component doesn't listen to it.
}

private addVersionSwitcher(menuData: any[]) {
  // Don't add version switcher to the menu data array
  // We'll add it as a separate item in the template
  return menuData;
}

private toggleSection(sectionId: string) {
  // Find the section to check if it's locked
  const menuItems = this.getMenuItems as any[];
  const section = menuItems.find((s: any) => s.section === sectionId);

  // If section is locked, trigger upgrade modal instead of expanding
  if (section && section.permissionRequired && !this.hasPermission(section.permissionCode)) {
    // Track locked feature click for sections
    const requiredPackage = this.getRequiredPackage(section);
    this.eventHandler.trackLockedFeatureClicked(section.title, requiredPackage);

    this.selectedLockedFeature = {
      ...section,
      currentPackage: this.store.currentPackage.name,
      requiredPackage,
    };

    // Track upgrade modal shown
    this.upgradeModalShownTime = Date.now();
    this.eventHandler.trackUpgradeModalShown(section.title, 'locked_feature');
    this.showUpgradeModal = true;
    return;
  }

  // Track menu section click for non-locked sections
  const timeSinceLoad = Date.now() - this.pageLoadTime;
  this.eventHandler.trackMenuItemClicked(this.menuVersionStore.menuVersion, section?.title || sectionId, timeSinceLoad);

  const currentState = this.expandedState.sections[sectionId];

  // Track manual interaction
  if (currentState) {
    this.manuallyCollapsed.add(sectionId);
  } else {
    this.manuallyCollapsed.delete(sectionId);
  }

  // Collapse all other sections and their groups
  Object.keys(this.expandedState.sections).forEach((key) => {
    this.$set(this.expandedState.sections, key, false);
  });
  Object.keys(this.expandedSections).forEach((key) => {
    this.$set(this.expandedSections, key, false);
  });
  // Also collapse all groups when switching sections
  Object.keys(this.expandedState.groups).forEach((key) => {
    this.$set(this.expandedState.groups, key, false);
  });

  // Toggle current section
  this.$set(this.expandedState.sections, sectionId, !currentState);
  this.$set(this.expandedSections, sectionId, !currentState); // Keep old one for compatibility

  // If we're expanding the section, also expand all groups within it
  if (!currentState && section) {
    // Use nextTick to ensure Vue has updated the DOM for the section expansion
    this.$nextTick(() => {
      // Expand all groups in this section
      section.items?.forEach((item: any) => {
        if (item.type === 'group' && !this.isItemLocked(item)) {
          const groupKey = `${sectionId}.${item.id || item.title}`;
          this.$set(this.expandedState.groups, groupKey, true);
          // Remove from manually collapsed since we're auto-expanding
          this.manuallyCollapsed.delete(groupKey);
        }
      });
      // Force update and save state
      this.$forceUpdate();
      this.saveExpandedState();
    });
  }

  // Save state
  this.saveExpandedState();
}

private toggleSubSection(sectionId: string, groupId?: string) {
  if (!groupId) {
    // Old behavior for compatibility
    Object.keys(this.expandedSections).forEach((key) => {
      if (key !== sectionId) {
        this.$set(this.expandedSections, key, false);
      }
    });
    return;
  }

  const groupKey = `${sectionId}.${groupId}`;
  const currentState = this.expandedState.groups[groupKey];

  // Track manual interaction
  if (currentState) {
    this.manuallyCollapsed.add(groupKey);
  } else {
    this.manuallyCollapsed.delete(groupKey);
  }

  // Toggle group
  this.$set(this.expandedState.groups, groupKey, !currentState);

  // Ensure parent section is expanded
  if (!currentState && !this.expandedState.sections[sectionId]) {
    this.$set(this.expandedState.sections, sectionId, true);
    this.$set(this.expandedSections, sectionId, true); // Compatibility
  }

  // Save state
  this.saveExpandedState();
}

private async loadForecastMethodology() {
  try {
    await this.forecastSetting.load();
    this.currentMethodology = this.forecastSetting.options?.methodology || 'simple_average';
  } catch (error) {
    this.currentMethodology = 'simple_average';
  }
}

@Watch('store.id')
private async onStoreChange(newStoreId: number, oldStoreId: number): Promise<void> {
  // When store changes, reload pinned items for the new store
  if (newStoreId && newStoreId !== oldStoreId) {
    await this.pinnedItemsStore.clearPinnedItems();
    await this.pinnedItemsStore.loadPinnedItems(newStoreId);
  }
}

@Watch('$route')
private onRouteChange(newRoute: any): void {
  // Don't run on initial load - let created() handle that
  // This only updates when route changes during normal navigation

  // The menu expansion state is already handled by user clicks
  // We don't need to auto-expand based on route
  // Just let the user's manual interactions control the menu state
}

private openChat(): void {
  const openChatMessage = 'I would like to unlock the Replenishment Center feature';
  // @ts-ignore: Unreachable code error
  this.$crisp.do('chat:open');
  // @ts-ignore: Unreachable code error
  this.$crisp.do('message:send', ['text', openChatMessage]);
}

// Generate item ID for pinning (matches the format in Vuex module)
private generateItemId(section: string, itemTitle: string, groupTitle?: string): string {
  const sanitize = (str: string) => str
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const parts = [section];
  if (groupTitle) {
    parts.push(sanitize(groupTitle));
  }
  parts.push(sanitize(itemTitle));

  return parts.join('-');
}

private handleReportDrawerOpen(): void {
  this.mini = true;
  this.canExpandOnHover = true;
  EventBus.$emit('vertical-menu-pinned', false);
}

private handleReportDrawerClosed(): void {
  this.mini = false;
  this.canExpandOnHover = false;
  EventBus.$emit('vertical-menu-pinned', true);
  EventBus.$emit('vertical-menu-expanded'); // Emit new event
}

private initializeMenuVersion() {
  this.menuVersionStore.initializeMenuVersion();
}

private openExternalLink(url: string): void {
    window.open(url, '_blank');
  }

  private handleLearnMore(packageName: string): void {
    // Track Learn More click
    const timeOnModal = Date.now() - this.upgradeModalShownTime;
    this.eventHandler.trackUpgradeModalLearnMoreClicked(this.selectedLockedFeature?.title || '', timeOnModal);

    // Navigate to subscription page
    this.$router.push('/subscription');
  }

  // Dynamic menu path discovery methods
  private findMenuPathByRoute(route: string): {
    section?: string;
    sectionId?: string;
    group?: string;
    groupId?: string;
    item?: string;
  } | null {
    const menuItems = this.getMenuItems;

    for (const section of menuItems) {
      // Check direct section items
      for (const item of section.items || []) {
        if (item.type === 'group') {
          // Check sub-items in groups
          for (const subItem of item.items || []) {
            if (subItem.link === route) {
              return {
                section: section.title,      // Title for display/storage
                sectionId: section.section,  // ID for expandedState keys
                group: item.title,           // Title for display/storage
                groupId: item.id || item.title, // ID for expandedState keys
                item: subItem.title,          // Title for display/storage
              };
            }
          }
        } else if (item.link === route) {
          // Direct item under section
          return {
            section: section.title,      // Title for display/storage
            sectionId: section.section,  // ID for expandedState keys
            item: item.title,             // Title for display/storage
          };
        }
      }
    }

    // Check saved route mappings as fallback
    return this.loadRouteMenuPath(route);
  }

  // Track menu clicks to build route mapping dynamically
  private trackMenuNavigation(section: string, group?: string, item?: string, link?: string): void {
    // Update current path with titles only (no link to save space)
    // When group is undefined and item has a value, it means it's a group-level item
    this.currentMenuPath = {
      section,
      group: group || undefined,
      item: item || undefined,
    };

    // Save this route-to-path mapping for future use
    if (link) {
      // Extract base path without query params for mapping
      const basePath = link.split('?')[0];
      this.saveRouteMenuPath(basePath, { section, group, item });
    }

    // Immediately save the expanded state with updated currentPath
    this.saveExpandedState();
  }

  // Load route-to-menu-path mapping
  private loadRouteMenuPath(route: string): any {
    try {
      const storageKey = `menu_routes_${this.store.id}`;
      const existing = localStorage.getItem(storageKey);
      if (existing) {
        const routeMap = JSON.parse(existing);
        return routeMap[route] || null;
      }
    } catch (error) {
      console.warn('Failed to load route mapping:', error);
    }
    return null;
  }

  // Save route-to-menu-path mapping
  private saveRouteMenuPath(route: string, path: any): void {
    try {
      const storageKey = `menu_routes_${this.store.id}`;
      const existing = localStorage.getItem(storageKey);
      const routeMap = existing ? JSON.parse(existing) : {};
      routeMap[route] = { ...path, timestamp: Date.now() };

      // Keep only last 100 routes to prevent unlimited growth
      const routes = Object.keys(routeMap);
      if (routes.length > 100) {
        delete routeMap[routes[0]];
      }

      localStorage.setItem(storageKey, JSON.stringify(routeMap));
    } catch (error) {
      console.warn('Failed to save route mapping:', error);
    }
  }

  private getMenuVersion(): number {
    let menuVersion = 1;
    const menuVersionStore = this.menuVersionStore?.menuVersion;
    if (!menuVersionStore) {
      const savedVersion = localStorage.getItem('menuVersion');
      menuVersion = savedVersion ? parseInt(savedVersion, 10) as 1 | 2 : 1;
    }
    return menuVersion;
  }
  // Get unique storage key per store and menu version
  private getStorageKey(): string {
    // Get menu version from localStorage if menuVersionStore is not properly initialized
    const storeId = this.store.id;
    const menuVersion = this.getMenuVersion();
    return `menu_state_${storeId}_v${menuVersion}`;
  }

  // Save expanded state
  private saveExpandedState(): void {
    try {
      // Get the actual menu version
      const menuVersion = this.getMenuVersion();

      const state = {
        sections: this.expandedState.sections,
        groups: this.expandedState.groups,
        manuallyCollapsed: Array.from(this.manuallyCollapsed),
        currentPath: this.currentMenuPath,
        timestamp: Date.now(),
        version: menuVersion,  // Use the actual menu version instead of hardcoding 1
      };
      localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save menu state:', error);
    }
  }

  // Load expanded state
  private loadExpandedState(): void {
    try {
      const saved = localStorage.getItem(this.getStorageKey());
      if (saved) {
        const state = JSON.parse(saved);
        this.expandedState.sections = state.sections || {};

        // Handle both old format (just group name) and new format (section.group)
        const groups = state.groups || {};
        const convertedGroups: { [key: string]: boolean } = {};

        // Convert old format to new format if needed
        Object.keys(groups).forEach((key) => {
          if (key.includes('.')) {
            // Already in new format
            convertedGroups[key] = groups[key];
          } else {
            // Old format - need to find which section this group belongs to
            // Use currentPath to determine the section if available
            if (state.currentPath && state.currentPath.section && state.currentPath.group === key) {
              convertedGroups[`${state.currentPath.section}.${key}`] = groups[key];
            } else {
              // Try to find the group in the menu structure
              const menuItems = this.getMenuItems;
              for (const section of menuItems) {
                for (const item of section.items || []) {
                  if (item.type === 'group' && (item.id === key || item.title === key)) {
                    convertedGroups[`${section.section}.${key}`] = groups[key];
                    break;
                  }
                }
              }
            }
          }
        });

        this.expandedState.groups = convertedGroups;
        this.manuallyCollapsed = new Set(state.manuallyCollapsed || []);
        this.currentMenuPath = state.currentPath || {};

        // If we have a currentPath with a group and item, ensure that group is expanded
        if (this.currentMenuPath.section && this.currentMenuPath.group && this.currentMenuPath.item) {
          // Find the section in the menu
          const menuItems = this.getMenuItems;
          const section = menuItems.find((s) => s.title === this.currentMenuPath.section);
          if (section && section.section) {
            // Expand the section
            this.$set(this.expandedState.sections, section.section, true);
            this.$set(this.expandedSections, section.section, true);

            // Find and expand the group
            const group = section.items?.find((g: any) => g.title === this.currentMenuPath.group);
            if (group && group.type === 'group') {
              const groupKey = `${section.section}.${group.id || group.title}`;
              this.$set(this.expandedState.groups, groupKey, true);

              // Remove from manually collapsed if it was there
              this.manuallyCollapsed.delete(groupKey);
            }
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load menu state:', error);
      this.expandedState = { sections: {}, groups: {} };
    }
  }

  // Check if item is the current selected item
  private isCurrentItem(itemTitle: string): boolean {
    if (!itemTitle || !this.currentMenuPath.item) { return false; }

    // Check if this item title matches the current item in currentMenuPath
    return this.currentMenuPath.item === itemTitle;
  }

  // Cleanup old route mappings
  private cleanupOldRouteMappings(): void {
    try {
      const storageKey = `menu_routes_${this.store.id}`;
      const existing = localStorage.getItem(storageKey);
      if (existing) {
        const routeMap = JSON.parse(existing);
        const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

        // Remove entries older than a week
        Object.keys(routeMap).forEach((route) => {
          if (routeMap[route].timestamp && routeMap[route].timestamp < oneWeekAgo) {
            delete routeMap[route];
          }
        });

        localStorage.setItem(storageKey, JSON.stringify(routeMap));
      }
    } catch (error) {
      console.warn('Failed to cleanup route mappings:', error);
    }
  }
}
